import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, Users, MapPin, Clock, DollarSign, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentBatches = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await fetch(`${API_URL}/api/batches`);
      const data = await response.json();
      setBatches(data);
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleEnroll = async (batchId, batchName, fees) => {
    const token = localStorage.getItem('studentToken');
    if (!token) {
      alert('Please login first');
      navigate('/student/login');
      return;
    }

    setEnrolling(batchId);

    try {
      const response = await fetch(`${API_URL}/api/enrollments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ batch_id: batchId })
      });

      const orderData = await response.json();

      if (!response.ok) {
        alert(orderData.detail || 'Enrollment failed');
        setEnrolling(null);
        return;
      }

      // Initialize Razorpay payment
      const options = {
        key: 'rzp_test_your_key',  // Will be updated
        amount: orderData.amount * 100,
        currency: orderData.currency,
        name: 'Vidya365',
        description: `Enrollment for ${orderData.batch_name}`,
        order_id: orderData.razorpay_order_id,
        handler: async function (response) {
          // Verify payment
          const verifyRes = await fetch(`${API_URL}/api/enrollments/verify-payment`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              enrollment_id: orderData.enrollment_id
            })
          });

          const verifyData = await verifyRes.json();

          if (verifyRes.ok) {
            alert(verifyData.message);
            navigate('/student/dashboard');
          } else {
            alert('Payment verification failed');
          }
        },
        prefill: {
          name: localStorage.getItem('studentUser') ? JSON.parse(localStorage.getItem('studentUser')).full_name : '',
          email: localStorage.getItem('studentUser') ? JSON.parse(localStorage.getItem('studentUser')).email : ''
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
      setEnrolling(null);
    } catch (error) {
      console.error('Enrollment error:', error);
      alert('Enrollment failed. Please try again.');
      setEnrolling(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-gradient-to-b from-red-50 to-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 text-center mb-4">
            Available Batches
          </h1>
          <p className="text-xl text-gray-600 text-center max-w-3xl mx-auto">
            Choose the perfect batch for your exam preparation journey
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <Loader2 className="animate-spin mx-auto text-red-600" size={48} />
            </div>
          ) : batches.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Calendar className="mx-auto text-gray-400 mb-4" size={48} />
                <p className="text-gray-600">No batches available</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {batches.map((batch) => (
                <Card key={batch.id} className="hover:shadow-xl transition-shadow">
                  <CardHeader className="bg-red-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="px-3 py-1 bg-red-600 text-white rounded-full text-xs font-medium">
                          {batch.course_category}
                        </span>
                        <CardTitle className="text-xl mt-3">{batch.name}</CardTitle>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        batch.batch_type === 'online' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {batch.batch_type}
                      </span>
                    </div>
                    <CardDescription className="mt-2">{batch.course_name}</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 space-y-3">
                    <p className="text-sm text-gray-600">{batch.description}</p>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-gray-700">
                        <Clock className="mr-2 text-red-600" size={16} />
                        <span>{batch.timing}</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <Calendar className="mr-2 text-red-600" size={16} />
                        <span>{batch.days.join(', ')}</span>
                      </div>
                      {batch.location && (
                        <div className="flex items-center text-gray-700">
                          <MapPin className="mr-2 text-red-600" size={16} />
                          <span>{batch.location}</span>
                        </div>
                      )}
                      <div className="flex items-center text-gray-700">
                        <Users className="mr-2 text-red-600" size={16} />
                        <span>{batch.enrolled_count}/{batch.max_students} enrolled</span>
                      </div>
                      <div className="flex items-center text-gray-700">
                        <span className="mr-2 text-red-600">👨‍🏫</span>
                        <span>{batch.instructor_name}</span>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl font-bold text-red-600">₹{batch.fees}</span>
                        <span className="text-sm text-gray-500">Course Fee</span>
                      </div>
                      <Button
                        onClick={() => handleEnroll(batch.id, batch.name, batch.fees)}
                        disabled={enrolling === batch.id || batch.enrolled_count >= batch.max_students}
                        className="w-full bg-red-600 hover:bg-red-700"
                      >
                        {enrolling === batch.id ? (
                          <>
                            <Loader2 className="mr-2 animate-spin" size={16} />
                            Processing...
                          </>
                        ) : batch.enrolled_count >= batch.max_students ? (
                          'Batch Full'
                        ) : (
                          'Enroll Now'
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StudentBatches;
